import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { 
  Visibility as ViewIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { formatVendorCurrency } from '../utils/currency';
import { useAuth } from '../contexts/AuthContext';
import InvoicePreview from './InvoicePreview';
import CustomInvoiceModal from '../components/CustomInvoiceModal';

interface VendorInvoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  period_start: string;
  period_end: string;
  opening_balance: number;
  total_billing: number;
  total_payments: number;
  net_amount: number;
  currency: string;
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'final' | 'sent' | 'paid' | 'overdue';
  notes: string;
  vendorFirm: {
    id: number;
    name: string;
  };
  vendorUser: {
    id: number;
    name: string;
    currency?: string | null;
  };
  createdBy: {
    id: number;
    name: string;
  };
  items: VendorInvoiceItem[];
}

interface VendorInvoiceItem {
  id: number;
  case_title: string;
  billing_type: string;
  rate: number;
  quantity: number;
  amount: number;
  description: string;
}

const MyInvoices: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<VendorInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<VendorInvoice | null>(null);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    period_start: new Date().toISOString().split('T')[0].substring(0, 7) + '-01',
    period_end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
    notes: '',
  });
  const [generateLoading, setGenerateLoading] = useState(false);
  const [showCustomInvoiceModal, setShowCustomInvoiceModal] = useState(false);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/vendor-invoices/my-invoices');
      if (response.data.success) {
        console.log('My invoices data:', response.data.data.data);
        setInvoices(response.data.data.data || []);
      }
    } catch (err: any) {
      console.error('Error loading invoices:', err);
      setError(err.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveInvoice = async (invoiceId: number) => {
    try {
      const response = await api.post(`/vendor-invoices/${invoiceId}/approve`);
      if (response.data.success) {
        setSuccess('Invoice sent for PMP admin approval');
        loadInvoices();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send invoice for approval');
    }
  };

  const handleRejectInvoice = async (invoiceId: number) => {
    try {
      const response = await api.post(`/vendor-invoices/${invoiceId}/reject`);
      if (response.data.success) {
        setSuccess('Invoice approval request cancelled');
        loadInvoices();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to cancel approval request');
    }
  };

  const handlePreviewInvoice = (invoice: VendorInvoice) => {
    // Always navigate to custom-invoices page for both regular and custom invoices
    navigate(`/custom-invoices/${invoice.id}`);
  };

  const handlePreviewMyInvoice = async () => {
    try {
      setPreviewLoading(true);
      const response = await api.get('/vendor-invoices/preview-my-invoice', {
        params: {
          period_start: generateForm.period_start,
          period_end: generateForm.period_end,
        }
      });
      if (response.data.success) {
        setPreviewInvoice(response.data.data);
        setShowPreviewDialog(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleGenerateInvoice = async () => {
    try {
      setGenerateLoading(true);
      const response = await api.post('/vendor-invoices/generate-my-invoice', generateForm);
      if (response.data.success) {
        setSuccess('Invoice generated successfully in draft status');
        setShowGenerateDialog(false);
        setGenerateForm({
          period_start: new Date().toISOString().split('T')[0].substring(0, 7) + '-01',
          period_end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
          notes: '',
        });
        loadInvoices();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate invoice');
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleDownloadPdf = async (invoiceId: number) => {
    try {
      const response = await api.get(`/vendor-invoices/${invoiceId}/download`, {
        responseType: 'blob',
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'invoice.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('Invoice downloaded successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download invoice');
    }
  };

  const handleViewInvoice = (invoice: VendorInvoice) => {
    // Always navigate to custom-invoices page for both regular and custom invoices
    navigate(`/custom-invoices/${invoice.id}`);
  };

  const handleCustomInvoiceCreated = (invoice: VendorInvoice) => {
    setSuccess('Custom invoice created successfully! You can now add custom items.');
    loadInvoices(); // Refresh the list
    
    // Navigate to the custom invoice view page where they can add custom items
    navigate(`/custom-invoices/${invoice.id}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'overdue': return 'error';
      case 'sent': return 'warning';
      case 'draft': return 'default';
      case 'pending_approval': return 'warning';
      case 'approved': return 'info';
      case 'rejected': return 'error';
      case 'final': return 'success';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number, invoice?: VendorInvoice) => {
    const currency = invoice?.vendorUser?.currency;
    return formatVendorCurrency(amount, currency);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">My Invoices</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setShowGenerateDialog(true)}
          >
            Generate Invoice
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setShowCustomInvoiceModal(true)}
          >
            Create Custom Invoice
          </Button>
          <Typography variant="body2" color="text.secondary">
            Welcome, {user?.name}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Invoices
            </Typography>
            <Typography variant="h4">
              {invoices.length}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Amount
            </Typography>
            <Typography variant="h4">
              {formatCurrency(invoices.reduce((sum, inv) => sum + (inv.net_amount || 0), 0), invoices[0])}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Paid Invoices
            </Typography>
            <Typography variant="h4">
              {invoices.filter(inv => inv.status === 'paid').length}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Pending Amount
            </Typography>
            <Typography variant="h4">
              {formatCurrency(invoices.filter(inv => inv.status !== 'paid').reduce((sum, inv) => sum + (inv.net_amount || 0), 0), invoices[0])}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Invoices Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Total Billing</TableCell>
                <TableCell>Net Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No invoices found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>
                      <Typography fontWeight="bold">
                        {invoice.invoice_number || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {invoice.period_start ? new Date(invoice.period_start).toLocaleDateString() : 'N/A'} - {invoice.period_end ? new Date(invoice.period_end).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>{formatCurrency(invoice.total_billing || 0, invoice)}</TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">
                        {formatCurrency(invoice.net_amount || 0, invoice)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Chip
                          label={invoice.status === 'pending_approval' ? 'SENT FOR APPROVAL' : 
                                 invoice.status === 'final' ? 'FINALIZED' :
                                 invoice.status.toUpperCase()}
                          color={getStatusColor(invoice.status) as any}
                          size="small"
                        />
                        {/* Show custom invoice indicator */}
                        {invoice.items.some(item => !item.case_title || item.case_title === '') && (
                          <Chip
                            label="CUSTOM"
                            color="secondary"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Invoice">
                          <IconButton
                            size="small"
                            onClick={() => handleViewInvoice(invoice)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {invoice.status === 'draft' && (
                          <Tooltip title="Preview Invoice">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handlePreviewInvoice(invoice)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                                                  {invoice.status === 'draft' && (
                            <Tooltip title="Send for PMP Admin Approval">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleApproveInvoice(invoice.id)}
                              >
                                <SendIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {invoice.status === 'pending_approval' && (
                            <Tooltip title="Cancel Approval Request">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleRejectInvoice(invoice.id)}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        {(invoice.status === 'final' || invoice.status === 'approved') && (
                          <Tooltip title="Download PDF">
                            <IconButton 
                              size="small"
                              onClick={() => handleDownloadPdf(invoice.id)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Invoice Details Dialog */}
      <Dialog open={showInvoiceDialog} onClose={() => setShowInvoiceDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Invoice Details - {selectedInvoice?.invoice_number}
        </DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Invoice Number</Typography>
                  <Typography variant="body1">{selectedInvoice.invoice_number}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip
                    label={selectedInvoice.status.toUpperCase()}
                    color={getStatusColor(selectedInvoice.status) as any}
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Period</Typography>
                  <Typography variant="body1">
                    {selectedInvoice.period_start ? new Date(selectedInvoice.period_start).toLocaleDateString() : 'N/A'} - {selectedInvoice.period_end ? new Date(selectedInvoice.period_end).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Invoice Date</Typography>
                  <Typography variant="body1">
                    {selectedInvoice.invoice_date ? new Date(selectedInvoice.invoice_date).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Opening Balance</Typography>
                  <Typography variant="body1">{formatCurrency(selectedInvoice.opening_balance || 0, selectedInvoice)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Total Billing</Typography>
                  <Typography variant="body1">{formatCurrency(selectedInvoice.total_billing || 0, selectedInvoice)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Total Payments</Typography>
                  <Typography variant="body1">{formatCurrency(selectedInvoice.total_payments || 0, selectedInvoice)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Net Amount</Typography>
                  <Typography variant="body1" fontWeight="bold">{formatCurrency(selectedInvoice.net_amount || 0, selectedInvoice)}</Typography>
                </Box>
              </Box>

              {selectedInvoice.notes && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                  <Typography variant="body1">{selectedInvoice.notes}</Typography>
                </Box>
              )}

              <Typography variant="h6" sx={{ mb: 2 }}>Invoice Items</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Case</TableCell>
                      <TableCell>Billing Type</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Rate</TableCell>
                      <TableCell>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedInvoice.items?.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.case_title || 'N/A'}</TableCell>
                        <TableCell>{item.billing_type || 'N/A'}</TableCell>
                        <TableCell>{item.quantity || 0}</TableCell>
                        <TableCell>{formatCurrency(item.rate || 0, selectedInvoice)}</TableCell>
                        <TableCell>{formatCurrency(item.amount || 0, selectedInvoice)}</TableCell>
                      </TableRow>
                    )) || []}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedInvoice?.status === 'draft' && (
            <>
              <Button
                variant="outlined"
                color="info"
                startIcon={<VisibilityIcon />}
                onClick={() => {
                  handlePreviewInvoice(selectedInvoice);
                  setShowInvoiceDialog(false);
                }}
              >
                Preview Invoice
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={() => {
                  handleApproveInvoice(selectedInvoice.id);
                  setShowInvoiceDialog(false);
                }}
              >
                Send for PMP Admin Approval
              </Button>
            </>
          )}
          <Button onClick={() => setShowInvoiceDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Generate Invoice Dialog */}
      <Dialog open={showGenerateDialog} onClose={() => setShowGenerateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate New Invoice</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Period Start"
              type="date"
              value={generateForm.period_start}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGenerateForm({ ...generateForm, period_start: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Period End"
              type="date"
              value={generateForm.period_end}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGenerateForm({ ...generateForm, period_end: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Notes (Optional)"
              multiline
              rows={3}
              value={generateForm.notes}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGenerateForm({ ...generateForm, notes: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="info"
            onClick={handlePreviewMyInvoice}
            disabled={generateLoading}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateInvoice}
            disabled={generateLoading}
          >
            {generateLoading ? <CircularProgress size={20} /> : 'Generate Invoice'}
          </Button>
          <Button onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      {/* Invoice Preview Dialog */}
      <InvoicePreview
        open={showPreviewDialog}
        onClose={() => setShowPreviewDialog(false)}
        invoice={previewInvoice}
        allowRateEditing={true}
        onInvoiceUpdated={(updatedInvoice) => {
          setPreviewInvoice(updatedInvoice);
        }}
      />

      {/* Custom Invoice Modal */}
      <CustomInvoiceModal
        open={showCustomInvoiceModal}
        onClose={() => setShowCustomInvoiceModal(false)}
        onInvoiceCreated={handleCustomInvoiceCreated}
      />
    </Box>
  );
};

export default MyInvoices;
